import { createRecordingRecoveryRepository } from './recording-recovery.js';
import { recordingHealth, recoverableRecordings } from '../svelte/store.js';

export function createRecordingSafetyService({ repository, healthStore, recoverableStore, now = Date.now }) {
  if (!repository) throw new TypeError('repository is required');

  async function begin(metadata) {
    return repository.begin(metadata);
  }

  async function write(recordingId, chunk) {
    const startedAt = now();
    try {
      const manifest = await repository.write(recordingId, chunk);
      const writeLatencyMs = Math.max(0, now() - startedAt);
      healthStore.update(current => ({
        ...current,
        signals: { ...(current?.signals || {}), writeFailed: false },
        metrics: {
          ...(current?.metrics || {}),
          chunks: manifest.chunks,
          bytes: manifest.bytes,
          lastChunkAt: manifest.lastChunkAt,
          writeLatencyMs
        }
      }));
      return manifest;
    } catch (error) {
      healthStore.update(current => ({
        ...current,
        status: 'action-required',
        signals: { ...(current?.signals || {}), writeFailed: true, message: 'Recording chunk could not be secured.' }
      }));
      throw error;
    }
  }

  async function progress(recordingId, chunkBytes, healthEvidence = null) {
    const startedAt = now();
    try {
      const manifest = await repository.progress(recordingId, chunkBytes, healthEvidence);
      const writeLatencyMs = Math.max(0, now() - startedAt);
      healthStore.update(current => ({
        ...current,
        signals: { ...(current?.signals || {}), writeFailed: false },
        metrics: {
          ...(current?.metrics || {}),
          chunks: manifest.chunks,
          bytes: manifest.bytes,
          lastChunkAt: manifest.lastChunkAt,
          writeLatencyMs
        }
      }));
      return manifest;
    } catch (error) {
      healthStore.update(current => ({
        ...current,
        status: 'action-required',
        signals: { ...(current?.signals || {}), writeFailed: true, message: 'Recording progress could not be secured.' }
      }));
      throw error;
    }
  }

  async function finalize(recordingId) {
    return repository.finalize(recordingId);
  }

  async function refreshRecoverable() {
    const recordings = await repository.listRecoverable();
    recoverableStore.set(recordings);
    return recordings;
  }

  async function confirmSaved(recordingId) {
    await repository.confirmSaved(recordingId);
    await refreshRecoverable();
  }

  async function remove(recordingId) {
    await repository.remove(recordingId);
    await refreshRecoverable();
  }

  return { begin, progress, write, finalize, refreshRecoverable, confirmSaved, remove };
}

let browserServicePromise = null;

export function getBrowserRecordingSafetyService() {
  if (browserServicePromise) return browserServicePromise;
  browserServicePromise = (async () => {
    const storageManager = globalThis.navigator?.storage;
    const manifestStorage = globalThis.localStorage;
    if (!storageManager?.getDirectory || !manifestStorage) return null;
    const storageRoot = await storageManager.getDirectory();
    const repository = createRecordingRecoveryRepository({ storageRoot, manifestStorage });
    return createRecordingSafetyService({ repository, healthStore: recordingHealth, recoverableStore: recoverableRecordings });
  })().catch(error => {
    browserServicePromise = null;
    console.warn('Recording recovery storage is unavailable:', error);
    return null;
  });
  return browserServicePromise;
}

export async function loadRecoverableRecordings() {
  const service = await getBrowserRecordingSafetyService();
  if (!service) {
    recoverableRecordings.set([]);
    return [];
  }
  return service.refreshRecoverable();
}

export async function beginRecoverableRecording(metadata) {
  return (await getBrowserRecordingSafetyService())?.begin(metadata) || null;
}

export async function writeRecoverableChunk(recordingId, chunk) {
  const service = await getBrowserRecordingSafetyService();
  if (!service) return null;
  return service.write(recordingId, chunk);
}

export async function updateRecoverableProgress(recordingId, chunkBytes, healthEvidence = null) {
  const service = await getBrowserRecordingSafetyService();
  if (!service) return null;
  return service.progress(recordingId, chunkBytes, healthEvidence);
}

export async function finalizeRecoverableRecording(recordingId) {
  const service = await getBrowserRecordingSafetyService();
  if (!service) return null;
  return service.finalize(recordingId);
}

export async function confirmRecoverableRecordingSaved(recordingId) {
  return (await getBrowserRecordingSafetyService())?.confirmSaved(recordingId);
}

export async function deleteRecoverableRecording(recordingId) {
  return (await getBrowserRecordingSafetyService())?.remove(recordingId);
}
